/* global Modernizr, TurbolinksAnimate */

import $ from 'jquery';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import Headroom from 'headroom.js';
import hoverintent from 'hoverintent';
import objectFitImages from 'object-fit-images';
import Plyr from 'plyr';
import PubSub from 'pubsub-js';
import { slideUp } from 'es6-slide-up-down';
import Rellax from 'rellax';
import Swiper from 'swiper';
import trim from 'lodash/trim';
import Turbolinks from 'turbolinks';
import 'turbolinks-animate';
import zenscroll from 'zenscroll';
import 'what-input';

import MQHelper from './mq-helper';
import parseQueryString from './parse-query-string';
import './once';
import './resizestartstop';
import './vh-check';

import './jquery.mousewheel';

const mq = new MQHelper({ watch: true });

Turbolinks.start();

const selectors = {
  document,
  window,

  html: 'html',
  body: 'body',

  navigationToggle: '.js-navigation-toggle',
  anchorLinks: 'a[href^="#"]',

  content: '.js-content',

  splash: '.js-splash',
  splashClose: '.js-splash-close',

  projectList: '.js-project-list',
  projectListDirection: '.js-project-list-direction',
  projectListContent: '.js-project-list-content',
  projectListSection: '.js-project-list-section',
  projectListItem: '.js-project-list-item',
  projectListSectionInfo: '.js-project-list-section-info',

  langSwitcher: '.js-lang-switcher',
  langContent: '.js-lang-content',

  tableLink: '.js-table-link',

  parallaxTitle: '.js-parallax-title',

  video: '.js-video',

  announcement: '.js-announcement',
  announcementClose: '.js-announcement-close',
};

const config = {
  smoothScrollingSpeed: 600,
  objectFitInterval: 3000,
  transitionDuration: 0,
  breakpoints: {},
};

export default class App {

  constructor(_config = {}) {

    // Merge defaults and passed config object
    this.config = {
      ...config,
      ..._config,
    };

    // Read shared config from CSS
    this.readCSSConfig();

    // On DOM loaded: init all events and sub-modules
    $(this.init.bind(this));
  }

  init() {
    // Add breakpoints to MQ helper
    mq.addQueries(this.config.breakpoints);

    // Cache elements for later use
    this.cacheElements();

    // Pub/sub and event bindings
    this.setupEvents();

    // Polyfills
    this.polyfillObjectFit();

    // Modules
    this.setupHeader();
    this.setupNavigation();
    this.setupContent();
    this.setupTurboLinks();
    this.setupSmoothAnchorScrolling();
    this.setupExternalLinks();
    this.setupSlideshows();
    this.setupLanguageSwitcher();
    this.markBodyDuringResize();
    this.setupSplashScreen();
    this.setupProjectList();
    this.setupTableLinks();
    this.setupParallaxTitles();
    this.setupVideoPlayers();
    this.setupAnnouncements();

    // Mark page as loaded
    this.$e.html.removeClass('preload');
  }

  // Fetch elements for all defined selectors

  cacheElements() {
    this.selectors = selectors;
    this.elements = Object.assign({}, ...Object.keys(this.selectors).map(k => ({ [k]: $(k) })));
    this.$e = this.elements;
  }

  // Events and pub/sub for interaction with other modules

  setupEvents() {
    // Update CSS config on mediaquery change
    PubSub.subscribe('mediaquery.change', this.readCSSConfig.bind(this));

    // Custom event on mediaquery change
    PubSub.subscribe('mediaquery.change', (/* event, mq */) => {
      console.log('Breakpoint', mq.current);
    });

    // Ajax behaviour and state changes
    $(document).on('beforecache', this.beforeDomCache.bind(this));
    $(document).on('statechange', this.onStateChange.bind(this));

    // "Fake" state change to initialize everything now
    this.onStateChange();
  }

  // Configure turbolinks

  setupTurboLinks() {

    // Move turbolinks events into general namespace

    $(document).on('turbolinks:before-cache', () => {
      $(document).trigger('beforecache');
    });
    $(document).on('turbolinks:load', () => {
      $(document).trigger('statechange');
    });

    // Enable page transitions

    $(document).on('turbolinks:load', () => {
      TurbolinksAnimate.init({ animation: 'fadein' });
    });

    // Pre-fetch links on hover

    $(document).on('turbolinks:load', () => {
      this.prefetchInternalLinks();
    });
    this.prefetchInternalLinks();
  }

  // Prefetch internal links on hover

  prefetchInternalLinks() {
    let $prefetcher;

    $('a[href^="/"]:not([data-turbolinks=false])')
      .once('prefetch')
      .each((index, node) => {
        hoverintent(node, () => {
          const href = node.getAttribute('href');
          if ($prefetcher) {
            if ($prefetcher.attr('href') !== href) {
              $prefetcher.attr('href', href);
            }
          }
          else {
            $prefetcher = $('<link>').attr('rel', 'prefetch').attr('href', href).appendTo('header');
          }
        }, () => {});
      });
  }

  beforeDomCache() {
    $('.lazyloaded').removeClass('.lazyloaded');
    $('body').removeClass('is-navigation-visible');
  }

  // Handler that executes after every page load (http or ajax)

  onStateChange() {
    this.trackPageView();
    this.scrollToCurrentHash();
  }

  // Check if page has template (body class name: template-xxx)

  pageHasTemplate(template, element = null) {
    const templates = Array.isArray(template) ? template : [template];
    const selector = templates.map(t => `.template-${t}`).join(',');
    return $(element || 'body').is(selector);
  }

  // Read and initialize all shared CSS config values

  readCSSConfig() {
    // Default transition duration in ms
    this.config.transitionDuration = this.getCSSConfigValue('transition') || 0;

    // Breakpoints / media queries
    if (Object.keys(this.config.breakpoints).length === 0) {
      const cssBreakpoints = this.getCSSConfigValue('breakpoints');
      this.config.breakpoints = parseQueryString(cssBreakpoints);
    }

    PubSub.publish('css.readconfig');
  }

  // Read shared config from CSS via <meta> tag's font-family

  getCSSConfigValue(key) {
    let configStr = $(`.js-css-config[data-key="${key}"]`).css('font-family');
    configStr = trim(configStr.trim(), '"\''); // browsers re-quote string styles
    return configStr;
  }

  // Polyfill object-fit for images, if necessary

  polyfillObjectFit() {
    if (Modernizr.objectfit) return;

    const triggerObjectFit = () => {
      objectFitImages(null, { watchMQ: true });
    };

    triggerObjectFit();
    setTimeout(triggerObjectFit, config.objectFitInterval / 2);
    setInterval(triggerObjectFit, config.objectFitInterval);
  }

  // Track page view

  trackPageView(location = null) {
    if (typeof window.ga === 'function') {
      const page = location || (window.location.pathname + window.location.search);
      window.ga('set', 'location', page);
      window.ga('send', 'pageview');
    }
  }

  // Track custom Google Analytics events

  trackCustomEvent(category, action, label) {
    if (typeof window.ga === 'function') {
      window.ga('send', 'event', category, action, label);
    }
  }

  // Add body class while resizing window

  markBodyDuringResize() {
    $(window).on('resizestart', () => {
      this.$e.html.addClass('is-resizing');
    });
    $(window).on('resizestop', () => {
      this.$e.html.removeClass('is-resizing');
    });
  }

  // Open external links in new tab

  setupExternalLinks() {
    const processLinks = () => {
      $('a')
        .once('external')
        .filter('[href^="http"], [href^="//"]')
        .not(`[href*="${window.location.host}"]`)
        .attr('rel', 'noopener noreferrer')
        .attr('target', '_blank');
    };

    $(document).on('statechange', processLinks);
    processLinks();
  }

  // Swiper slideshow

  setupSlideshows() {
    const process = () => {
      $('.swiper-container')
        .once('swiper')
        .each((i, node) => {
          const $slideshow = $(node);
          const slides = $('.swiper-slide', node);
          const isInteractive = slides.length > 1;
          const isAutoplay = isInteractive && $slideshow.is('[data-autoplay]');

          const swiperInstance = new Swiper(node, {
            speed: 500,
            spaceBetween: 40,
            grabCursor: true,
            autoHeight: true,
            autoplay: isAutoplay,
            loop: isInteractive,
            simulateTouch: isInteractive,
            allowTouchMove: isInteractive,
            navigation: {
              nextEl: '.js-slideshow-next',
              prevEl: '.js-slideshow-prev',
            },
            pagination: {
              el: '.swiper-pagination',
              clickable: true,
            },
            breakpoints: {
              320: { spaceBetween: 10 },
              500: { spaceBetween: 20 },
            },
          });
        });
    };
    $(document).on('statechange', process);
    process();
  }

  // Language switcher / body texts

  setUserLanguage(lang) {
    this.$e.html.attr('data-user-lang', lang);
    this.userLanguage = lang;
  }

  getUserLanguage() {
    return this.$e.html.attr('data-user-lang');
  }

  setupLanguageSwitcher() {
    this.userLanguage = this.getUserLanguage();

    const process = () => {
      const $contents = $(selectors.langContent).once('lang');
      const $switcher = $(selectors.langSwitcher).once('lang');
      const $triggers = $switcher.find('[data-lang]');

      const switchLang = (lang) => {
        this.setUserLanguage(lang);
        const $langContent = $contents.filter(`[data-lang=${lang}]`);
        const $visibleContent = $langContent.length ? $langContent : $contents;
        const visibleLang = $visibleContent.data('lang');
        const $visibleTrigger = $triggers.filter(`[data-lang=${visibleLang}]`);
        $contents.hide();
        $visibleContent.first().show();
        $triggers.removeClass('is-active');
        $visibleTrigger.addClass('is-active');
      };

      $triggers.on('click', (event) => {
        switchLang($(event.target).data('lang'));
        const lang = $(event.target).data('lang');
        this.setUserLanguage(lang);
      });

      switchLang(this.userLanguage);
    };
    $(document).on('statechange', process);
    process();
  }

  // Scroll to hash / anchor

  scrollTo(hash) {
    const anchor = hash.replace('#', '');

    let $target = $(`#${anchor}`);
    $target = $target.length ? $target : $(`#anchor__${anchor}`);
    $target = $target.length ? $target : $(`[name="${anchor}"]`);

    if ($target.length) {
      const scrollElem = document.scrollingElement || document.body;
      const scroller = zenscroll.createScroller(scrollElem, 0, 0);
      scroller.to($target.get(0), this.config.smoothScrollingSpeed);
      return true;
    }

    return false;
  }

  // Scroll to current hash

  scrollToCurrentHash() {
    const hash = window.location.hash.replace('#', '');
    if (hash) this.scrollTo(hash);
  }

  // Make anchor links scroll smoothly

  setupSmoothAnchorScrolling() {
    zenscroll.setup(this.config.smoothScrollingSpeed, 0);

    $(document).on('click', this.selectors.anchorLinks, (event) => {
      if (!window.noZensmooth) return;
      const $anchor = $(event.target).closest(this.selectors.anchorLinks);
      const href = $anchor.get(0).hash;
      if (this.scrollTo(href)) {
        event.preventDefault();
      }
    });
  }

  // Toggle navigation on button click

  setupNavigation() {
    $(document).on('click', this.selectors.navigationToggle, (event) => {
      $('body').toggleClass('is-navigation-visible');
      event.preventDefault();
    });
  }

  // Mark content offset

  setupContent() {
    const calcSizes = () => {
      const $content = $(selectors.content);
      const content = $content.get(0);
      const parentHeight = $content.parent().outerHeight(true);
      const bottom = $content.position().top + $content.outerHeight(true);
      const pseudoAfterHeight = parseInt(window.getComputedStyle(content, ':after').height, 10);

      const offset = Math.round(parentHeight - bottom + pseudoAfterHeight);
      document.documentElement.style.setProperty('--content-offset', `${offset}px`);
    };
    $(window).on('resizestop', calcSizes);
    calcSizes();
  }

  // Hide/show header based on scroll behaviour

  setupHeader() {
    const headroom = new Headroom(document.documentElement);
    headroom.init();
  }

  // Close splash on button click

  isSplashScreenVisible() {
    return $(this.selectors.splash).length && !$('html').hasClass('is-splash-hidden');
  }

  setupSplashScreen() {
    let redirectTo;
    const hide = (event = null) => {
      if (event) event.preventDefault();
      if (redirectTo) {
        window.location.href = redirectTo;
      }
      else {
        $('body').addClass('is-splash-hidden');
        $(document).trigger('splashclose');
      }
    };

    $(document).on('click', this.selectors.splashClose, hide);

    const process = () => {
      const $splash = $(this.selectors.splash).once('splash');
      if ($splash.length) {
        redirectTo = $splash.data('redirect');
        setTimeout(() => {
          $splash.addClass('is-animating');
        }, 3000);
      }
      // Failsafe if visitor doesn't click close button
      setTimeout(hide, 12000);
    };

    $(document).on('statechange', process);
    process();
  }

  setupProjectList() {
    const getVisibleSectionUnthrottled = ($sections) => {
      const mark = $(window).height() / 2;

      const distances = $sections.map((_, element) => {
        const { top, bottom } = element.getBoundingClientRect();
        let distance = Math.min(
          Math.abs(top - mark),
          Math.abs(bottom - mark),
        );
        if (top < mark && bottom > mark) {
          distance = 0;
        }
        return { element, distance };
      }).get();

      const nearest = distances.reduce((prev, curr) => (prev.distance < curr.distance ? prev : curr), distances[0]);

      return $(nearest.element).closest(selectors.projectListSection);
    };

    const getVisibleSection = throttle(getVisibleSectionUnthrottled, 100);

    const toggleScrolling = ($list, toggle) => {
      const scrolling = !!toggle;
      $list.toggleClass('is-scrolling', scrolling);
      $list.toggleClass('is-not-scrolling', !scrolling);
    };

    const process = () => {
      const $list = $(selectors.projectList).once('list');
      const $directions = $list.find(selectors.projectListDirection);
      const $vertical = $directions.eq(0);
      const $horizontal = $directions.eq(1);
      const $verticalSections = $vertical.find(selectors.projectListSection);
      const $horizontalSections = $horizontal.find(selectors.projectListSection);

      const vertical = $vertical.get(0);
      const horizontal = $horizontal.get(0);

      const $sections = $verticalSections;
      const $sectionInfos = $(selectors.projectListSectionInfo);
      if ($list.data('introduction')) {
        $sectionInfos.first().attr('data-section-title', $list.data('introduction'));
      }
      $sectionInfos.first().addClass('is-active');

      // Switch out section info when section scrolled into view
      $vertical.on('scroll', () => {
        const $activeSection = getVisibleSection($sections);
        const sectionName = $activeSection.data('section');
        const $activeInfo = $sectionInfos.filter(`[data-section='${sectionName}']`).first();
        $sectionInfos.not($activeInfo).removeClass('is-active');
        $activeInfo.addClass('is-active');
      });

      let prevTop = 0;
      let prevLeft = 0;
      let direction = '';

      toggleScrolling($list, false);

      let maxHeight = 0;
      let maxWidth = 0;

      const calcSizes = () => {
        const windowWidth = $(window).width();
        const windowHeight = $(window).height();

        let height = 0;
        let width = 0;

        $sections.each((index) => {
          const $verticalSection = $verticalSections.eq(index);
          const $horizontalSection = $horizontalSections.eq(index);

          let verticalHeight = 0;
          let horizontalWidth = 0;
          $verticalSection.find(selectors.projectListItem).each((_, node) => {
            verticalHeight += $(node).outerHeight();
          });
          $horizontalSection.find(selectors.projectListItem).each((_, node) => {
            horizontalWidth += $(node).outerWidth();
          });

          const verticalNormal = verticalHeight / windowHeight;
          const horizontalNormal = horizontalWidth / windowWidth;

          const verticalIsLarger = verticalNormal > horizontalNormal;
          const verticalMin = !verticalIsLarger ? horizontalNormal * windowHeight : null;
          const horizontalMin = verticalIsLarger ? verticalNormal * windowWidth : null;
          $verticalSection.css('min-height', verticalMin ? `${verticalMin}px` : '');
          $horizontalSection.css('min-width', horizontalMin ? `${horizontalMin}px` : '');

          height += $verticalSection.outerHeight();
          width += $horizontalSection.outerWidth();
        });

        maxHeight = height;
        maxWidth = width;
      };
      $(window).on('resizestop', calcSizes);
      calcSizes();

      $vertical.on('scroll', debounce(() => {
        if (!direction) direction = 'vertical';
        toggleScrolling($list, true);
      }, 200, { leading: true, trailing: false }));

      $vertical.on('scroll', debounce(() => {
        direction = '';
        toggleScrolling($list, false);
      }, 250));

      $vertical.on('scroll', () => {
        if (direction !== 'vertical') return;

        const pos = vertical.scrollTop / (vertical.scrollHeight - vertical.offsetHeight);
        if (pos !== prevTop) {
          horizontal.scrollLeft = pos * (horizontal.scrollWidth - horizontal.offsetWidth);
        }
        prevTop = pos;
      });

      $horizontal.on('scroll', debounce(() => {
        if (!direction) direction = 'horizontal';
        toggleScrolling($list, true);
      }, 200, { leading: true, trailing: false }));

      $horizontal.on('scroll', debounce(() => {
        direction = '';
        toggleScrolling($list, false);
      }, 250));

      $horizontal.on('scroll', () => {
        if (direction !== 'horizontal') return;

        const pos = horizontal.scrollLeft / (horizontal.scrollWidth - horizontal.offsetWidth);
        if (pos !== prevLeft) {
          vertical.scrollTop = pos * (vertical.scrollHeight - vertical.offsetHeight);
        }
        prevLeft = pos;
      });

      // Set scrolling mode to on when using mousewheel or swiping

      $list.on('mousewheel', () => {
        toggleScrolling($list, true);
        $vertical.stop();
      });
      $list.on('touchmove', () => {
        toggleScrolling($list, true);
        $vertical.stop();
      });

      // Auto-scroll immediately or when splash is closed
      const animate = () => {
        const duration = maxHeight / 50 * 1000;
        $vertical.animate({ scrollTop: maxHeight }, duration, 'linear');
      };

      if (this.isSplashScreenVisible()) {
        $(document).on('splashclose', animate);
      } else {
        animate();
      }
    };

    $(document).on('statechange', process);
    process();
  }

  setupTableLinks() {
    const process = () => {
      const $links = $(selectors.tableLink).once('link');
      $links.each((_, node) => {
        const $link = $(node);
        const url = $link.data('url');
        if (url) {
          $link.css('cursor', 'pointer');
          $link.find('a').on('click', (event) => {
            event.stopPropagation();
          });
          $link.on('click', () => {
            Turbolinks.visit(url);
          });
        }
      });
    };
    $(document).on('statechange', process);
    process();
  }

  setupParallaxTitles() {
    const process = () => {
      const $titles = $(selectors.parallaxTitle).once('parallax');
      $titles.each((_, node) => {
        // eslint-disable-next-line
        new Rellax(node, { speed: 6 });
      });
    };
    $(document).on('statechange', process);
    process();
  }

  setupVideoPlayers() {
    const process = () => {
      const $videos = $(selectors.video).once('plyr');
      $videos.each((_, node) => {
        // eslint-disable-next-line
        const player = new Plyr(node, {
          debug: true,
          controls: ['play-large', 'play', 'progress', 'mute', 'volume'],
          hideControls: true,
        });

        // Get container, set as "initial", remove on first play
        player.on('ready', (event) => {
          event.target.classList.add('plyr--initial');
        });
        player.on('playing', (event) => {
          event.target.classList.remove('plyr--initial');
        });
      });
    };
    $(document).on('statechange', process);
    process();
  }

  setupAnnouncements() {
    $(document).on('click', this.selectors.announcementClose, (event) => {
      const $announcement = $(event.target).closest(selectors.announcement);
      if ($announcement.length) {
        const duration = 800;
        const $inner = $announcement.children().first();
        $inner.addClass('is-hiding');
        $announcement.addClass('is-hiding');
        slideUp($inner.get(0), duration);
      }
    });
  }
}
